import { FC, useCallback, useMemo } from 'react';
import { useDispatch, useSelector, Provider } from 'react-redux';
import { I18nextProvider } from 'react-i18next';

import alertTypes from 'constants/alertTypes';
import { State } from 'redux/initialState';
import { store } from 'redux/store';
import { addAlert } from 'modules/_shared/redux/alertActions';
import InputCheckbox from 'modules/_shared/components/InputCheckbox';
import { useTranslate } from 'hooks/useTranslate';
import i18n from 'i18n/config';
import { formatCurrency, formatNumber } from 'utils/formats';
import errorSwal from 'utils/errorSwal';
import { Partner } from 'types';

import { isShareClassVisible } from 'modules/trading/utils/isShareClassVisible';
import EquityMarketTermsByDefault from 'modules/trading/pages/EquityMarketTerms/components/EquityMarketTermsByDefault';
import EquityMarketTermsByTemplate from 'modules/trading/pages/EquityMarketTerms/components/EquityMarketTermsByTemplate';
import EquityMarketPurchaseContractPreview from 'modules/trading/components/EquityMarketPurchaseContractPreview';

import { EQUITY_MARKET_DEFAULT_PAYMENT_DEADLINE } from 'modules/trading/constants/equityMarket';
import { useAcceptTradingOfferModal } from '../context/AcceptTradingOfferModalContext';

const AcceptTradingOfferModalStep3: FC = () => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const { data, actions } = useAcceptTradingOfferModal();

  const actualSociety = useSelector(
    ({ society }: State) => society?.actualSociety
  );

  const paymentDeadline =
    actualSociety?.configuration?.equityMarket?.payment
      ?.paymentDeadlineBusinessDays ?? EQUITY_MARKET_DEFAULT_PAYMENT_DEADLINE;

  const publisherPartnerForPreview = useMemo((): Partial<Partner> | null => {
    const p = data.offer.partner;
    if (typeof p === 'object' && p !== null) {
      const nameFromOffer =
        typeof p.name === 'string' && p.name.trim().length > 0
          ? p.name
          : data.sellerName;
      return {
        ...p,
        name: nameFromOffer,
      };
    }
    const id = typeof p === 'string' ? p : undefined;
    if (!id && !data.sellerName) return null;
    return {
      _id: id,
      name: data.sellerName,
    };
  }, [data.offer.partner, data.sellerName]);

  const publisherPartnerId = useMemo(() => {
    const p = data.offer.partner;
    if (typeof p === 'string') return p;
    return p?._id || '';
  }, [data.offer.partner]);

  const handleShowTerms = () => {
    const templateId = actualSociety?.templates?.equityMarketTermsAndConditions;

    dispatch(
      addAlert({
        type: alertTypes.NONE,
        title: `<h5 class="modal-title">${i18n.t(
          'OfferConditionsPageTitle'
        )}</h5>`,
        html: (
          <Provider store={store}>
            <I18nextProvider i18n={i18n}>
              {templateId ? (
                <EquityMarketTermsByTemplate templateId={templateId} />
              ) : (
                <EquityMarketTermsByDefault />
              )}
            </I18nextProvider>
          </Provider>
        ),
        props: {
          width: 'min(900px, 95vw)',
          confirmButtonText: i18n.t('Close'),
          onOpen: (popup: HTMLElement) => {
            const htmlContainer = popup.querySelector(
              '.swal2-html-container'
            ) as HTMLElement | null;
            if (htmlContainer) {
              htmlContainer.style.margin = '0';
              htmlContainer.style.padding = '0';
            }
          },
        },
      })
    );
  };

  const handleShowPurchaseContract = useCallback(
    (e: { preventDefault(): void }) => {
      e.preventDefault();
      const templateId = actualSociety?.templates?.equityMarketPurchaseContract;
      const societyId = data.offer.society;

      if (!templateId || !societyId || !publisherPartnerId) {
        errorSwal(t('ErrorNoEquityMarketTemplate'));
        return;
      }

      dispatch(
        addAlert({
          type: alertTypes.NONE,
          title: `<h5 class="modal-title">${t(
            'EquityMarketPurchaseContractPreviewTitle'
          )}</h5>`,
          html: (
            <Provider store={store}>
              <I18nextProvider i18n={i18n}>
                <EquityMarketPurchaseContractPreview
                  templateId={templateId}
                  societyId={societyId}
                  partnerId={publisherPartnerId}
                  offerType={data.offer.type}
                  previewVariant="accept"
                  language={i18n.language}
                  operationReferencePlaceholder={
                    data.offerReference ||
                    t('EquityMarketPurchaseContractDraftReference')
                  }
                  publisherPartner={publisherPartnerForPreview}
                  participantPartner={data.partnerBuyer}
                  purchaseQuantity={data.purchaseQuantity}
                  sharePrice={data.offer.sharePrice}
                  contractClassName={data.className}
                />
              </I18nextProvider>
            </Provider>
          ),
          props: {
            width: 'min(1200px, 95vw)',
            confirmButtonText: t('Close'),
            onOpen: (popup: HTMLElement) => {
              const htmlContainer = popup.querySelector(
                '.swal2-html-container'
              ) as HTMLElement | null;
              if (htmlContainer) {
                htmlContainer.style.margin = '0';
                htmlContainer.style.padding = '0';
              }
            },
          },
        })
      );
    },
    [
      actualSociety?.templates?.equityMarketPurchaseContract,
      data.offer.society,
      data.offer.type,
      data.offer.sharePrice,
      data.partnerBuyer,
      data.purchaseQuantity,
      data.className,
      data.offerReference,
      dispatch,
      publisherPartnerId,
      publisherPartnerForPreview,
      t,
    ]
  );

  const totalPrice = data.purchaseQuantity * data.offer.sharePrice;

  return (
    <div className="nk-stepper-step active">
      <div className="text-center mb-4">
        <em className="icon ni ni-check-circle fs-24px text-success" />
        <h5 className="mt-2">{t('ConfirmPurchase')}</h5>
        <p className="text-soft">{t('ReviewAndAcceptTermsToFinalize')}</p>
      </div>

      <div className="card card-bordered bg-success-dim mb-4">
        <div className="card-inner text-center">
          <h4 className="text-success mb-1">{formatCurrency(totalPrice)}</h4>
          <p className="mb-0">
            {formatNumber(data.purchaseQuantity)} {t('Shares').toLowerCase()}
            {isShareClassVisible() ? ` ${data.className}` : ''} {t('At')}{' '}
            {formatCurrency(data.offer.sharePrice)}/{t('Share').toLowerCase()}
          </p>
        </div>
      </div>

      <div className="alert alert-light">
        <h6 className="alert-heading fs-14px">{t('OperationTerms')}</h6>
        <ul className="fs-13px mb-0 list-bulleted">
          <li>{t('OperationTermItem1')}</li>
          <li>{t('OperationTermItem2', { days: paymentDeadline })}</li>
          <li>{t('OperationTermItem3')}</li>
          <li>{t('OperationTermItem4')}</li>
        </ul>
      </div>

      <div className="mt-4">
        <div className="mb-2">
          <InputCheckbox
            id="accept-terms-checkbox"
            label={t('AcceptTermsAndConditionsOfOperation')}
            checked={data.isTermsAccepted}
            onChange={actions.handleToggleTerms}
            onLinkClick={handleShowTerms}
            linkLabel={t('ViewConditions')}
          />
        </div>
        <div className="custom-control custom-checkbox mb-2">
          <input
            type="checkbox"
            className="custom-control-input"
            id="accept-offer-purchase-contract-checkbox"
            checked={data.isPurchaseContractAccepted}
            onChange={actions.handleTogglePurchaseContract}
          />
          <label
            className="custom-control-label"
            htmlFor="accept-offer-purchase-contract-checkbox"
          >
            {t('AcceptOfferAcceptPurchaseContractIntro')}{' '}
            <button
              type="button"
              className="btn btn-link p-0 align-baseline"
              onClick={handleShowPurchaseContract}
            >
              {t('EquityMarketPurchaseContractShort')}
            </button>
            {t('AcceptOfferAcceptPurchaseContractOutro')}
          </label>
        </div>
        <div className="mb-2">
          <InputCheckbox
            id="accept-irrevocable-checkbox"
            label={t('UnderstandIrrevocableAcceptance')}
            checked={data.isIrrevocableAccepted}
            onChange={actions.handleToggleIrrevocable}
          />
        </div>
        <div>
          <InputCheckbox
            id="accept-fiscal-checkbox"
            label={t('AwareFiscalObligations')}
            checked={data.isFiscalAccepted}
            onChange={actions.handleToggleFiscal}
          />
        </div>
      </div>
    </div>
  );
};

export default AcceptTradingOfferModalStep3;
